import { StreamMessageType } from '../protocol/StreamMessage';
import { Scaffold } from '../utils/Scaffold';
import { Signal } from '../utils/Signal';
/**
 * Manages adding & removing subscriptions to node as needed.
 * A session contains one or more subscriptions to a single streamId + streamPartition pair.
 */
const getAnyItemFromSet = (set) => {
    return set.values().next().value;
};
export class SubscriptionSession {
    streamPartId;
    onRetired = Signal.once();
    isRetired = false;
    isStopped = false;
    subscriptions = new Set();
    pendingRemoval = new WeakSet();
    pipeline;
    node;
    constructor(streamPartId, messagePipelineFactory, node) {
        this.streamPartId = streamPartId;
        this.distributeMessage = this.distributeMessage.bind(this);
        this.node = node;
        this.onError = this.onError.bind(this);
        this.pipeline = messagePipelineFactory.createMessagePipeline({
            streamPartId
        });
        this.pipeline.onError.listen(this.onError);
        this.pipeline
            .pipe(this.distributeMessage)
            .onBeforeFinally.listen(async () => {
            if (!this.isStopped) {
                await this.stop();
            }
        });
        this.pipeline.flow();
    }
    async retire() {
        if (this.isRetired) {
            return;
        }
        this.isRetired = true;
        await this.onRetired.trigger();
    }
    async onError(error) {
        // eslint-disable-next-line promise/no-promise-in-callback
        await Promise.allSettled([...this.subscriptions].map(async (sub) => {
            await sub.handleError(error);
        }));
    }
    async *distributeMessage(src) {
        for await (const msg of src) {
            await Promise.all([...this.subscriptions].map(async (sub) => {
                await sub.push(msg);
            }));
            yield msg;
        }
    }
    onMessageInput = async (msg) => {
        if (!msg || this.isStopped || this.isRetired) {
            return;
        }
        if (msg.getStreamPartID() !== this.streamPartId) {
            return;
        }
        if (msg.messageType !== StreamMessageType.MESSAGE) {
            return;
        }
        const tasks = [];
        let hasNormalSubscriptions = false;
        for (const sub of this.subscriptions.values()) {
            if (sub.isRaw) {
                tasks.push(sub.push(msg));
            }
            else {
                hasNormalSubscriptions = true;
            }
        }
        if (hasNormalSubscriptions) {
            tasks.push(this.pipeline.push(msg));
        }
        await Promise.all(tasks);
    };
    async subscribe() {
        this.node.addMessageListener(this.onMessageInput);
        if (!await this.node.isProxiedStreamPart(this.streamPartId)) {
            const deliveryOptions = getAnyItemFromSet(this.subscriptions).deliveryOptions;
            await this.node.join(this.streamPartId, undefined, deliveryOptions);
        }
    }
    async unsubscribe() {
        this.pipeline.end();
        this.pipeline.return();
        this.pipeline.onError.end(new Error('done'));
        this.node.removeMessageListener(this.onMessageInput);
        await this.node.leave(this.streamPartId);
    }
    updateNodeSubscriptions = (() => {
        return Scaffold([
            async () => {
                await this.subscribe();
                return async () => {
                    await this.unsubscribe();
                    await this.stop();
                };
            },
        ], () => this.shouldBeSubscribed());
    })();
    async updateSubscriptions() {
        await this.updateNodeSubscriptions();
        if (!this.shouldBeSubscribed() && !this.isStopped) {
            await this.stop();
        }
    }
    shouldBeSubscribed() {
        return !this.isRetired && !this.isStopped && !!this.count();
    }
    async stop() {
        this.isStopped = true;
        this.pipeline.end();
        await this.retire();
        await this.pipeline.return();
    }
    has(sub) {
        return this.subscriptions.has(sub);
    }
    /**
     * Add subscription & appropriate connection handle.
     */
    async add(sub) {
        if (!sub || this.subscriptions.has(sub) || this.pendingRemoval.has(sub)) {
            return;
        } // already has
        const activeErc1271ContractAddress = this.getERC1271ContractAddress();
        if (this.subscriptions.size > 0 && activeErc1271ContractAddress !== sub.erc1271ContractAddress) {
            throw new Error('Subscription ERC-1271 mismatch');
        }
        this.subscriptions.add(sub);
        sub.onBeforeFinally.listen(() => {
            return this.remove(sub);
        });
        await this.updateSubscriptions();
    }
    /**
     * Remove subscription & appropriate connection handle.
     */
    async remove(sub) {
        if (!sub || this.pendingRemoval.has(sub) || !this.subscriptions.has(sub)) {
            return;
        }
        this.pendingRemoval.add(sub);
        this.subscriptions.delete(sub);
        try {
            if (!sub.isDone()) {
                await sub.unsubscribe();
            }
        }
        finally {
            await this.updateSubscriptions();
        }
    }
    getERC1271ContractAddress() {
        for (const sub of this.subscriptions) {
            if (sub.erc1271ContractAddress !== undefined) {
                return sub.erc1271ContractAddress;
            }
        }
        return undefined;
    }
    /**
     * How many subscriptions
     */
    count() {
        return this.subscriptions.size;
    }
}
